// DataFlowDiagram.js
import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import jsPDF from "jspdf";

const DataFlowDiagram = () => {
    const [records, setRecords] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("Logical");

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                setRecords(results.data);
                setError(null);
                setLoading(false);
            },
            error: (err) => {
                setError(`Failed to parse CSV: ${err.message}`);
                setLoading(false);
            },
        });
    };

    const handleDownloadPDF = () => {
        const canvas = document.getElementById("dataFlowCanvas");
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "pt", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const canvasRatio = canvas.width / canvas.height;
        const imgWidth = pageWidth;
        const imgHeight = pageWidth / canvasRatio;
        const totalPages = Math.ceil(imgHeight / pageHeight);

        for (let i = 0; i < totalPages; i++) {
            const sourceY = (i * pageHeight * canvas.height) / imgHeight;
            const pageCanvas = document.createElement("canvas");
            const pageCtx = pageCanvas.getContext("2d");

            pageCanvas.width = canvas.width;
            pageCanvas.height = (pageHeight * canvas.height) / imgHeight;

            pageCtx.drawImage(
                canvas,
                0,
                sourceY,
                canvas.width,
                pageCanvas.height,
                0,
                0,
                canvas.width,
                pageCanvas.height
            );

            const pageImgData = pageCanvas.toDataURL("image/png");
            if (i > 0) pdf.addPage();
            pdf.addImage(pageImgData, "PNG", 0, 0, imgWidth, pageHeight);
        }

        pdf.save("data-flow-diagram.pdf");
    };

    useEffect(() => {
        if (records.length === 0) return;
        drawDiagram(records, mode);
    }, [records, mode]);

    const drawDiagram = (records, mode) => {
        const canvas = document.getElementById("dataFlowCanvas");
        const ctx = canvas.getContext("2d");

        const groups = {};

        const getFirstNonEmpty = (key) => {
            const val = records.find(r => r[key] && r[key].trim());
            return val ? val[key].trim() : key;
        };

        const sor1SystemName = getFirstNonEmpty("SOR1 System");
        const sor2SystemName = getFirstNonEmpty("SOR2 System");
        const sor3SystemName = getFirstNonEmpty("SOR3 System");

        records.forEach((row) => {
            const srcKey = `${row["SOR1 System"] || ""} - ${row["SOR1 Entity"] || ""}`;
            const midKey = `${row["SOR2 System"] || ""} - ${row["SOR2 Entity"] || ""}`;
            const tgtKey = `${row["SOR3 System"] || ""} - ${row["SOR3 Entity"] || ""}`;
            const groupKey = `${srcKey}__${midKey}__${tgtKey}`;

            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(row);
        });

        const boxWidth = 250;
        const lineSpacing = 35;
        const boxPaddingTop = 20;
        const boxPaddingBottom = 20;
        const groupSpacing = 40;
        const startX = 100;
        const startY = 120;
        const colGap = 500;

        const totalLines = Object.values(groups).reduce((sum, rows) => sum + rows.length, 0);
        const estimatedHeight = totalLines * lineSpacing + Object.keys(groups).length * (boxPaddingTop + boxPaddingBottom + groupSpacing);
        canvas.width = 1600;
        canvas.height = estimatedHeight + 200;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw Title and Labels
        ctx.font = "bold 20px Arial";
        ctx.fillStyle = "black";
        const dynamicTitle = `${sor1SystemName} - ${sor2SystemName} - ${sor3SystemName} Data Flow Diagram`;
        const titleWidth = ctx.measureText(dynamicTitle).width;
        const dynamicCenterX = (canvas.width - titleWidth) / 2;
        ctx.fillText(dynamicTitle, dynamicCenterX, startY - 70);

        ctx.font = "bold 16px Arial";
        ctx.fillText(sor1SystemName, startX + boxWidth / 2 - ctx.measureText(sor1SystemName).width / 2, startY + 20);
        ctx.fillText(sor2SystemName, startX + colGap + boxWidth / 2 - ctx.measureText(sor2SystemName).width / 2, startY + 20);
        ctx.fillText(sor3SystemName, startX + 2 * colGap + boxWidth / 2 - ctx.measureText(sor3SystemName).width / 2, startY + 20);

        ctx.font = "italic 12px Arial";
        const legend = "[M] = Mapped Data   |   [API] = Synced with API   |   [FILE] = Synced with File   |   [WF Events] = Synced with Workflow Events   |   [Flows to API] = Synced with Flows calling Command APIs";
        const legendWidth = ctx.measureText(legend).width;
        const legendX = (canvas.width - legendWidth) / 2;
        ctx.fillText(legend, legendX, startY - 40);

        const docLine = "Full product documentation and mapping files can be found at https://help.anthology.com/INT/Content/tocIntegrations.htm";
        const docLineWidth = ctx.measureText(docLine).width;
        const docLineX = (canvas.width - docLineWidth) / 2;
        ctx.fillText(docLine, docLineX, startY - 20);

        let currentY = startY + 50;

        Object.entries(groups).forEach(([groupKey, rows]) => {
            const [srcLabel, midLabel, tgtLabel] = groupKey.split("__");

            const hasSOR1Data = rows.some(r => r["SOR1 System"] && r["SOR1 Entity"]);
            const hasSOR2Data = rows.some(r => r["SOR2 System"] && r["SOR2 Entity"]);
            const hasSOR3Data = rows.some(r => r["SOR3 System"] && r["SOR3 Entity"]);

            const lineCount = rows.length;
            const boxHeight = lineCount * lineSpacing + boxPaddingTop + boxPaddingBottom;
            const topY = currentY;

            const sourceLabel = mode === "Logical"
                ? rows[0]["SOR1 Entity"] || ""
                : rows[0]["SOR1 Db Table"] || "";

            const middleLabel = mode === "Logical"
                ? rows[0]["SOR2 Entity"] || ""
                : rows[0]["SOR2 Db Table"] || "";

            const targetLabel = mode === "Logical"
                ? rows[0]["SOR3 Entity"] || ""
                : (rows[0]["SOR3 Db Table"] && rows[0]["SOR3 Db Table"].trim()) || rows[0]["SOR3 Entity"] || "";

            const source = { label: sourceLabel, x: startX, y: topY };
            const middle = { label: middleLabel, x: startX + colGap, y: topY };
            const target = { label: targetLabel, x: startX + 2 * colGap, y: topY };

            const drawBox = (box, height) => {
                let fillColor = "#cce5ff";
                if (hasSOR1Data && hasSOR2Data && hasSOR3Data) fillColor = "#cce5ff";
                else if (hasSOR1Data && hasSOR2Data) fillColor = "#ffe0b2";
                else if (hasSOR2Data && hasSOR3Data) fillColor = "#ffffcc";

                ctx.fillStyle = fillColor;
                ctx.fillRect(box.x, box.y, boxWidth, height);
                ctx.strokeRect(box.x, box.y, boxWidth, height);
                ctx.fillStyle = "black";
                ctx.font = "bold 14px Arial";
                ctx.fillText(box.label, box.x + 10, box.y + 25);
            };

            if (hasSOR1Data) drawBox(source, boxHeight);
            if (hasSOR2Data) drawBox(middle, boxHeight);
            if (hasSOR3Data) drawBox(target, boxHeight);

            rows.forEach((row, i) => {
                const yOffset = topY + boxPaddingTop + i * lineSpacing;

                const sor1Field = mode === "Logical" ? row["SOR1 Field"] : row["SOR1 Db Field"];
                const sor2Field = mode === "Logical" ? row["SOR2 Field"] : row["SOR2 Db Field"];
                const sor3Field = mode === "Logical" ? row["SOR3 Field"] : row["SOR3 Db Field"];

                const dir = (row["SOR1 Data Flow Direction"] || "").trim();
                const dir1 = (row["SOR3 Data Flow Direction"] || "").trim();

                const sync = (row["SOR1 Sync Type"] || "").trim();
                const sync1 = (row["SOR3 Sync Type"] || "").trim();
                const mapped = (row["SOR1 Mapped"] || "").trim().toUpperCase();
                const mapped2 = (row["SOR3 Mapped"] || "").trim().toUpperCase();

                const defaultValue = (row["SOR1 Default"] || "").trim();
                const syncDisplay = sync
                    ? `[${sync}]${mapped === "Y" ? " [M]" : ""}${defaultValue ? ` (Default: ${defaultValue})` : ""}`
                    : "";

                const syncDisplay1 = sync1 ? `[${sync1}]${mapped2 === "Y" ? " [M]" : ""}` : "";

                if (hasSOR1Data && hasSOR2Data && dir) {
                    let arrow = "→";
                    if (dir === "SOR2 to SOR1") arrow = "←";
                    else if (dir === "Bi-Directional") arrow = "↔";
                    const label = `${sor1Field} ${arrow} ${sor2Field} ${syncDisplay}`;

                    if (arrow === "←") {
                        drawArrow(ctx, middle.x, yOffset, source.x + boxWidth, yOffset, label, source.x + boxWidth + 10, yOffset - 5);
                    } else if (arrow === "↔") {
                        drawArrow(ctx, source.x + boxWidth, yOffset, middle.x, yOffset, label, source.x + boxWidth + 5, yOffset - 5, true);
                    } else {
                        drawArrow(ctx, source.x + boxWidth, yOffset, middle.x, yOffset, label, source.x + boxWidth + 5, yOffset - 5);
                    }
                }

                if (hasSOR2Data && hasSOR3Data && dir1) {
                    let arrow = "→";
                    if (dir1 === "SOR3 to SOR2") arrow = "←";
                    else if (dir1 === "Bi-Directional") arrow = "↔";
                    const label = `${sor2Field} ${arrow} ${sor3Field} ${syncDisplay1}`;

                    if (arrow === "←") {
                        drawArrow(ctx, target.x, yOffset, middle.x + boxWidth, yOffset, label, middle.x + boxWidth + 10, yOffset - 5);
                    } else if (arrow === "↔") {
                        drawArrow(ctx, middle.x + boxWidth, yOffset, target.x, yOffset, label, middle.x + boxWidth + 5, yOffset - 5, true);
                    } else {
                        drawArrow(ctx, middle.x + boxWidth, yOffset, target.x, yOffset, label, middle.x + boxWidth + 5, yOffset - 5);
                    }
                }
            });

            currentY += boxHeight + groupSpacing;
        });
    };

    const drawArrow = (ctx, x1, y1, x2, y2, label, textX, textY, bidirectional = false) => {
        ctx.strokeStyle = "black";
        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        const angle = Math.atan2(y2 - y1, x2 - x1);
        const headlen = 7;

        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();

        if (bidirectional) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1 + headlen * Math.cos(angle - Math.PI / 6), y1 + headlen * Math.sin(angle - Math.PI / 6));
            ctx.lineTo(x1 + headlen * Math.cos(angle + Math.PI / 6), y1 + headlen * Math.sin(angle + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
        }

        ctx.font = "11px Arial";
        ctx.fillStyle = "black";
        ctx.fillText(label, textX, textY);
    };

    if (error) {
        return (
            <div className="bb-container bb-error">
                <h1 className="bb-title">Error</h1>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="bb-container" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h1 className="bb-title">Data Flow Diagram Generator</h1>
            <input type="file" accept=".csv" onChange={handleFileUpload} className="bb-input" />
            <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                <label htmlFor="modeSelect" style={{ marginRight: "10px" }}>Display Mode:</label>
                <select
                    id="modeSelect"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    style={{ padding: "5px" }}
                >
                    <option value="Logical">Logical</option>
                    <option value="Physical">Physical</option>
                </select>
            </div>
            <button onClick={handleDownloadPDF} style={{ marginTop: "10px", marginBottom: "10px" }}>
                Download PDF
            </button>
            {loading && <p>Loading data flow...</p>}
            {!loading && records.length === 0 && <p>Please upload a CSV file to begin.</p>}
            <div style={{ overflowX: "auto" }}>
                <canvas id="dataFlowCanvas" style={{ border: "1px solid #ccc", marginTop: "20px", display: "block" }} />
            </div>
        </div>
    );
};

export default DataFlowDiagram;
